import heic2any from 'heic2any';

const HEIC_MIME_TYPES = ['image/heic', 'image/heif', 'image/heic-sequence', 'image/heif-sequence'];

function getExtension(fileName: string = ''): string
{
    const parts = fileName.toLowerCase().split('.');
    return parts.length > 1 ? parts.pop() : '';
}

function isHeicExtension(fileName: string = ''): boolean
{
    const ext = getExtension(fileName);
    return ext === 'heic' || ext === 'heif';
}

export function isHeicMimeType(mimeType: string = ''): boolean
{
    return HEIC_MIME_TYPES.includes((mimeType || '').toLowerCase());
}

export function isHeicFile(file: File | Blob, fileName: string = ''): boolean
{
    return isHeicMimeType(file?.type) || isHeicExtension(fileName) || (typeof File !== 'undefined' && file instanceof File && isHeicExtension(file.name));
}

export async function convertHeicBlobToJpeg(blob: Blob): Promise<Blob>
{
    const converted = await heic2any({
        blob,
        toType: 'image/jpeg',
        quality: 0.92
    });

    return Array.isArray(converted) ? converted[0] : converted;
}

export async function normalizeImageBlobForUpload(blob: Blob, fileName: string = ''): Promise<Blob>
{
    if (!blob || !isHeicFile(blob, fileName))
    {
        return blob;
    }

    return convertHeicBlobToJpeg(blob);
}

export async function readBlobAsDataUrl(blob: Blob): Promise<string>
{
    return new Promise((resolve, reject) =>
    {
        const reader = new FileReader();
        reader.onerror = () => reject(reader.error);
        reader.onload = () => resolve(reader.result as string);
        reader.readAsDataURL(blob);
    });
}

export async function normalizeImageFileToDataUrl(file: File): Promise<string>
{
    const normalizedBlob = await normalizeImageBlobForUpload(file, file?.name);
    return readBlobAsDataUrl(normalizedBlob);
}

export async function loadImageElement(src: string): Promise<HTMLImageElement>
{
    return new Promise((resolve, reject) =>
    {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = (err) => reject(err);
        img.src = src;
    });
}

export async function getUploadBlobFromPhoto(
    photo: { webPath: string; path?: string },
    resizeImageForAndroid: boolean,
    maxWidth: number,
    maxHeight: number,
    resizeImage: (img: HTMLImageElement, width: number, height: number) => string
): Promise<Blob>
{
    const response = await fetch(photo.webPath);
    let blob = await response.blob();
    blob = await normalizeImageBlobForUpload(blob, photo.path || photo.webPath);

    if (!resizeImageForAndroid)
    {
        return blob;
    }

    const objectUrl = URL.createObjectURL(blob);
    try
    {
        const imgElement = await loadImageElement(objectUrl);

        var resizedImage = resizeImage(imgElement, maxWidth, maxHeight);
        resizedImage = resizedImage.replace("data:image/jpeg;base64,", '');

        const resizedResponse = await fetch("data:image/jpeg;base64," + resizedImage);
        return await resizedResponse.blob();
    }
    finally
    {
        URL.revokeObjectURL(objectUrl);
    }
}
